import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus, QrCode, RefreshCw, ScanLine, Server, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { licensingApi, type ResolveRegistrationRequestResponse } from '@/api/licensing';
import {
  approveMonitoringEnrollmentIntent,
  cancelMonitoringEnrollmentIntent,
  createMonitoringEnrollmentIntent,
  createMonitoringTarget,
  getMonitoringEnrollmentIntent,
  listMonitoringTargets,
  retryMonitoringEnrollmentIntent,
  type MonitoringEnrollmentView,
} from '@/api/monitoring';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { buildRegistrationApprovalContextKey, validateInstallationPayload } from './licensingViewModel';
import {
  isMonitoringEnrollmentTargetEligible,
  monitoringEnrollmentPresentation,
  monitoringEnrollmentProblemMessage,
} from './monitoringEnrollmentViewModel';

const APP_ID = 'doctor-server-host-agent';

function operationId(): string {
  return crypto.randomUUID();
}

function storageKey(clientId: number): string {
  return `monitoring-enrollment-intent:${clientId}`;
}

function readStoredIntent(clientId: number): string | null {
  try {
    return window.localStorage.getItem(storageKey(clientId));
  } catch {
    return null;
  }
}

function writeStoredIntent(clientId: number, intentId: string | null): void {
  try {
    if (intentId) window.localStorage.setItem(storageKey(clientId), intentId);
    else window.localStorage.removeItem(storageKey(clientId));
  } catch {
    // Status remains available for the current session even when storage is blocked.
  }
}

export function MonitoringEnrollmentFlow({ clientId, clientName }: {
  clientId: number;
  clientName?: string;
}) {
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState('');
  const [targetName, setTargetName] = useState('');
  const [locator, setLocator] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [resolved, setResolved] = useState<ResolveRegistrationRequestResponse | null>(null);
  const [confirmedContextKey, setConfirmedContextKey] = useState<string | null>(null);
  const [intentId, setIntentId] = useState<string | null>(() => readStoredIntent(clientId));
  const confirmationSubmitRef = useRef(false);

  const targets = useQuery({
    queryKey: ['monitoring', 'targets', clientId],
    queryFn: () => listMonitoringTargets(clientId),
  });
  const eligibleTargets = useMemo(
    () => (targets.data?.items ?? []).filter(isMonitoringEnrollmentTargetEligible),
    [targets.data],
  );

  const effectiveTargetId = eligibleTargets.some((target) => target.id === targetId)
    ? targetId
    : eligibleTargets.length === 1
      ? eligibleTargets[0].id
      : '';

  const status = useQuery({
    queryKey: ['monitoring', 'enrollment-intent', intentId],
    queryFn: () => {
      if (!intentId) throw new Error('Enrollment intent is not selected');
      return getMonitoringEnrollmentIntent(intentId);
    },
    enabled: Boolean(intentId),
    refetchInterval: (query) => {
      const view = query.state.data;
      return view?.intent.status === 'open' && view.presentation !== 'manual_recovery_required' ? 5_000 : false;
    },
    retry: false,
  });

  const installView = (view: MonitoringEnrollmentView) => {
    setIntentId(view.intent.id);
    writeStoredIntent(clientId, view.intent.id);
    queryClient.setQueryData(['monitoring', 'enrollment-intent', view.intent.id], view);
  };

  const createTargetMutation = useMutation({
    mutationFn: () => createMonitoringTarget({
      clientId,
      displayName: targetName.trim(),
      requestId: operationId(),
    }),
    onSuccess: async (target) => {
      setTargetName('');
      setConfirmedContextKey(null);
      await queryClient.invalidateQueries({ queryKey: ['monitoring', 'targets', clientId] });
      setTargetId(target.id);
      toast.success('Linux target создан');
    },
    onError: (error) => toast.error(monitoringEnrollmentProblemMessage(error)),
  });

  const resolveMutation = useMutation({
    mutationFn: (value: string) => licensingApi.resolveRegistrationRequest(value, clientId),
    onSuccess: (value) => {
      if (value.request.appId !== APP_ID || value.request.status !== 'PENDING') {
        setResolved(null);
        setConfirmedContextKey(null);
        toast.error('QR не относится к ожидающему Linux-агенту');
        return;
      }
      setResolved(value);
      setConfirmedContextKey(null);
      toast.success('Запрос регистрации найден');
    },
    onError: (error) => {
      setResolved(null);
      setConfirmedContextKey(null);
      toast.error(monitoringEnrollmentProblemMessage(error));
    },
  });

  const submitLocator = (value = locator) => {
    const checked = validateInstallationPayload(value);
    if (!checked.ok || checked.kind !== 'registration_request') {
      toast.error(checked.ok ? 'Нужен QR запроса регистрации DSLR1' : checked.message);
      return;
    }
    setLocator(checked.payload);
    resolveMutation.mutate(checked.payload);
  };

  const confirmationKey = resolved && effectiveTargetId
    ? buildRegistrationApprovalContextKey(resolved.request.id, resolved.request.appId, clientId, effectiveTargetId)
    : null;
  const confirmed = confirmationKey !== null && confirmedContextKey === confirmationKey;
  const view = status.data;

  const createIntentMutation = useMutation({
    mutationFn: async () => {
      if (confirmationSubmitRef.current) throw new Error('Подтверждение уже выполняется');
      if (!resolved || !effectiveTargetId || !confirmed || !locator) {
        throw new Error('Выберите target и подтвердите точные идентификаторы');
      }
      confirmationSubmitRef.current = true;
      if (view?.intent.status === 'open') {
        const sameContext = view.intent.target_id === effectiveTargetId
          && view.intent.client_id === clientId
          && view.intent.registration_request_id === resolved.request.id
          && view.intent.app_id === resolved.request.appId
          && view.intent.installation_id === resolved.request.installationId;
        if (!sameContext) {
          throw new Error('Сначала завершите или отмените текущую регистрацию');
        }
        if (view.intent.approval_phase !== 'none') {
          throw new Error('Подтверждение уже принято; обновите серверный статус');
        }
        return approveMonitoringEnrollmentIntent({
          intentId: view.intent.id,
          expectedVersion: view.intent.version,
          requestId: operationId(),
        });
      }
      const created = await createMonitoringEnrollmentIntent({
        targetId: effectiveTargetId,
        registrationLocator: locator,
        requestId: operationId(),
      });
      installView(created);
      return approveMonitoringEnrollmentIntent({
        intentId: created.intent.id,
        expectedVersion: created.intent.version,
        requestId: operationId(),
      });
    },
    onSuccess: (view) => {
      installView(view);
      setLocator('');
      setResolved(null);
      setConfirmedContextKey(null);
      toast.success('Регистрация подтверждена');
    },
    onError: (error) => toast.error(monitoringEnrollmentProblemMessage(error)),
    onSettled: () => {
      confirmationSubmitRef.current = false;
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => {
      const current = status.data;
      if (!current) throw new Error('Enrollment intent is not loaded');
      return retryMonitoringEnrollmentIntent({
        intentId: current.intent.id,
        expectedVersion: current.intent.version,
        requestId: operationId(),
      });
    },
    onSuccess: (view) => installView(view),
    onError: (error) => toast.error(monitoringEnrollmentProblemMessage(error)),
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      const current = status.data;
      if (!current) throw new Error('Enrollment intent is not loaded');
      return cancelMonitoringEnrollmentIntent({
        intentId: current.intent.id,
        expectedVersion: current.intent.version,
        requestId: operationId(),
      });
    },
    onSuccess: (view) => {
      installView(view);
      toast.success('Запрос на отмену обработан');
    },
    onError: (error) => toast.error(monitoringEnrollmentProblemMessage(error)),
  });

  const busy = createIntentMutation.isPending || retryMutation.isPending || cancelMutation.isPending;

  return (
    <section data-testid="monitoring-enrollment-flow" className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-start gap-3">
          <Server className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <h2 className="font-medium">Подключить Linux-сервер к мониторингу</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {clientName ? `Клиент «${clientName}» [${clientId}]` : `Клиент ${clientId}`}.
              Сначала выберите target, затем подтвердите QR агента.
            </p>
          </div>
        </div>

        <div data-testid="monitoring-enrollment-target" className="mt-4 grid gap-2">
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Monitoring target</span>
            <Select
              value={effectiveTargetId}
              onValueChange={(value) => {
                setTargetId(value);
                setConfirmedContextKey(null);
              }}
              disabled={targets.isLoading || busy}
            >
              <SelectTrigger aria-label="Выберите Linux monitoring target">
                <SelectValue placeholder={targets.isLoading ? 'Загрузка...' : 'Выберите target'} />
              </SelectTrigger>
              <SelectContent>
                {eligibleTargets.map((target) => (
                  <SelectItem key={target.id} value={target.id}>
                    {target.display_name} · {target.lifecycle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Input
              value={targetName}
              onChange={(event) => setTargetName(event.target.value)}
              placeholder="Новый Linux target"
              maxLength={160}
              disabled={createTargetMutation.isPending || busy}
              aria-label="Название нового Linux monitoring target"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => createTargetMutation.mutate()}
              disabled={!targetName.trim() || createTargetMutation.isPending || busy}
            >
              <Plus className="h-4 w-4" />
              Создать
            </Button>
          </div>
          {targets.isError && <p className="text-sm text-destructive">Targets временно недоступны.</p>}
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4">
        <div className="grid gap-2">
          <Button type="button" onClick={() => setScannerOpen(true)} disabled={busy}>
            <ScanLine className="h-4 w-4" />
            Сканировать QR агента
          </Button>
          <Textarea
            data-testid="monitoring-enrollment-scanner"
            value={locator}
            onChange={(event) => {
              setLocator(event.target.value);
              setResolved(null);
              setConfirmedContextKey(null);
            }}
            aria-label="QR или код регистрации Linux-агента"
            autoComplete="off"
            spellCheck={false}
            placeholder="DSLR1:..."
            className="min-h-24 font-mono text-xs"
            disabled={busy}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => submitLocator()}
            disabled={!locator.trim() || resolveMutation.isPending || busy}
          >
            <QrCode className="h-4 w-4" />
            Проверить код
          </Button>
        </div>

        {resolved && (
          <div data-testid="monitoring-enrollment-confirmation" className="mt-4 rounded-md border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Подтвердите точное назначение</p>
            <dl className="mt-2 grid gap-1 font-mono text-xs">
              <div>request: {resolved.request.id}</div>
              <div>app: {resolved.request.appId}</div>
              <div>client: {clientId}</div>
              <div>target: {effectiveTargetId || 'не выбран'}</div>
            </dl>
            <label className="mt-3 flex items-start gap-2">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmedContextKey(checked === true ? confirmationKey : null)}
                disabled={!confirmationKey || busy}
                aria-label="Подтвердить request, app, client и target"
              />
              <span>Все четыре идентификатора проверены; выбранный target относится к этому клиенту.</span>
            </label>
          </div>
        )}

        <Button
          type="button"
          className="mt-3 w-full"
          onClick={() => createIntentMutation.mutate()}
          disabled={!confirmed || busy}
        >
          <CheckCircle2 className="h-4 w-4" />
          Подтвердить регистрацию
        </Button>
      </div>

      <div data-testid="monitoring-enrollment-status" aria-live="polite" className="rounded-lg border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-medium">Статус подключения</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {status.isError ? monitoringEnrollmentProblemMessage(status.error) : monitoringEnrollmentPresentation(view)}
            </p>
          </div>
          {view && <Badge variant={view.intent.status === 'bound' ? 'visit' : 'outline'}>{view.intent.status}</Badge>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div data-testid="monitoring-enrollment-registration-axis" className="rounded-md bg-muted p-2">
            <p className="text-xs text-muted-foreground">Регистрация</p>
            <p className="font-medium">{view?.registration.status ?? 'нет'}</p>
          </div>
          <div data-testid="monitoring-enrollment-binding-axis" className="rounded-md bg-muted p-2">
            <p className="text-xs text-muted-foreground">Привязка</p>
            <p className="font-medium">{view?.binding.status ?? 'нет'}</p>
          </div>
        </div>
        {view?.presentation === 'manual_recovery_required' && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-500/10 dark:text-amber-100">
            manual_recovery_required: не повторяйте enroll и не создавайте новый QR. Передайте оператору ID intent.
          </div>
        )}
        {view?.error_code && <p className="mt-2 text-xs text-muted-foreground">Код: {view.error_code}</p>}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => retryMutation.mutate()}
            disabled={!view?.retryable || busy}
          >
            <RefreshCw className="h-4 w-4" />
            Повторить проверку
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => cancelMutation.mutate()}
            disabled={!view || view.intent.status !== 'open' || view.intent.cancellation_phase === 'cancelled' || busy}
          >
            <XCircle className="h-4 w-4" />
            Отменить регистрацию
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Отмена прекращает только этот запрос регистрации. Она не удаляет программу, станцию или накопленные данные.
        </p>
      </div>

      {scannerOpen && (
        <BarcodeScanner
          onScan={(value) => {
            setScannerOpen(false);
            setLocator(value);
            setResolved(null);
            setConfirmedContextKey(null);
            submitLocator(value);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </section>
  );
}
